import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import BulkActionsToolbar from 'modules/menu/bulk-actions/components';
import useBulkSelection from 'modules/menu/bulk-actions/useBulkSelection';
import { useItemsWithNoCategoryDurationViewTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuEditorLists from 'modules/menu/hooks/useMenuEditorLists';
import useMenuData from 'modules/menu/useMenuData';
import { isMenuEditorItem } from 'modules/menu/utils';

import { transformItemInventoryToQuantity } from './Item/utils';
import ItemSidePanel from './ItemSidePanel';
import { MenuItemsDnDMonitor } from './MenuItemsDnDMonitor';
import NewItemSidePanel from './NewItemSidePanel';
import MenuEditorCollectionList from '../../components/MenuEditorCollectionList';

type ItemsWithNoCategoryProps = {
  menuEditorRootUrl: string;
  onAssignNoCategoryItemsToCategory: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
};

const ItemsWithNoCategory = ({ onAssignNoCategoryItemsToCategory }: ItemsWithNoCategoryProps) => {
  const { menu } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const intl = useIntl();

  const {
    menuEditorList,
    activeItem,
    searchFilter,
    itemInventory,
    newItemSidePanelProps,
    setActiveItemId,
    setSearchFilter,
    handleItemCopy,
    refetchInventory,
    itemConfigs,
    updateItemConfig,
    itemCommissions,
    refetchItemCommissions,
  } = useMenuEditorLists('items-by-category');

  const { selectedIds, selectMultiple, deselectMultiple, clearSelection } = useBulkSelection(menuEditorList);

  const activeItemQuantity = useMemo(() => {
    const itemQuantity = itemInventory?.find(inventory => inventory.itemId === activeItem?.id);
    return itemQuantity ? transformItemInventoryToQuantity(itemQuantity) : undefined;
  }, [activeItem?.id, itemInventory]);

  const activeItemConfig = useMemo(() => {
    const activeItemId = activeItem?.id;
    return activeItemId ? itemConfigs[activeItemId] : undefined;
  }, [activeItem?.id, itemConfigs]);

  const itemsList = useMemo(() => menuEditorList.filter(isMenuEditorItem), [menuEditorList]);

  const allItemIds = useMemo(() => {
    return itemsList.map(item => item.itemState.id);
  }, [itemsList]);

  const handleSelectAllItems = useCallback(() => {
    selectMultiple(allItemIds);
  }, [allItemIds, selectMultiple]);

  // Remove ID from bulk selection if item is deleted or moved to another category
  useEffect(() => {
    const missingItemIds = [...selectedIds].filter(id => !allItemIds.includes(id));

    if (missingItemIds.length > 0) {
      deselectMultiple(missingItemIds);
    }
  }, [allItemIds, deselectMultiple, selectedIds]);

  const handleAssignNoCategoryItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>): Promise<void> => {
      await onAssignNoCategoryItemsToCategory(activeId, overId, selectedIds);
      if (selectedIds && selectedIds.size > 1) {
        clearSelection();
      }
    },
    [clearSelection, onAssignNoCategoryItemsToCategory],
  );
  useItemsWithNoCategoryDurationViewTracking();

  const handleItemClick = useCallback(
    (itemId: string) => setActiveItemId(itemId !== activeItem?.id ? itemId : undefined),
    [setActiveItemId, activeItem],
  );

  return (
    <MenuItemsDnDMonitor
      menu={menu}
      menuEditorList={menuEditorList}
      onAssignNoCategoryItemsToCategory={handleAssignNoCategoryItemsToCategory}
    >
      {({ draggingId, displayMenuEditorList }) => (
        <MenuEditorCollectionList
          title={intl.formatMessage({ id: 'menu.nonCategorised' })}
          description={intl.formatMessage({ id: 'menu.nonCategorisedDescription' })}
          search={searchFilter}
          onSearch={setSearchFilter}
          toolbar={
            <BulkActionsToolbar
              commissions={itemCommissions}
              onSelectAll={handleSelectAllItems}
              onClearSelection={clearSelection}
              disabled={!!activeItem}
              refetchCommissions={refetchItemCommissions}
              selectAllItemsCount={allItemIds.length}
              selectedIds={selectedIds}
            />
          }
          itemData={{
            menuId: menu.id,
            list: displayMenuEditorList,
            itemInventory,
            activeItemId: activeItem?.id,
            canDrag: true,
            selectedIds,
            draggingId,
            onSelectItems: selectMultiple,
            onDeselectItems: deselectMultiple,
            onItemClick: handleItemClick,
            onItemCopy: handleItemCopy,
          }}
        >
          {!!activeItem && (
            <ItemSidePanel
              item={activeItem}
              menu={menu}
              itemQuantity={activeItemQuantity}
              onItemBalanceUpdated={refetchInventory}
              onClose={() => setActiveItemId(undefined)}
              itemConfig={activeItemConfig}
              onItemConfigUpdated={updateItemConfig}
              itemCommissions={itemCommissions}
              onItemCommissionsUpdated={refetchItemCommissions}
            />
          )}
          {newItemSidePanelProps.isOpen && (
            <NewItemSidePanel {...newItemSidePanelProps} onItemConfigUpdated={updateItemConfig} />
          )}
        </MenuEditorCollectionList>
      )}
    </MenuItemsDnDMonitor>
  );
};

export default ItemsWithNoCategory;
